/* promo-links.js
   Usage: include on all pages (theme.liquid). It will:
   - Capture ?discount=CODE from the current URL and persist to localStorage
   - Append discount=CODE to all internal links (preserve existing query params)
   - Observe DOM changes and update new links
   - Avoids external links, mailto:, tel:, javascript:, and admin/checkout/account paths
*/

(function () {
  const PARAM_NAME = 'discount';                    // query param name to use
  const STORAGE_KEY = 'discountCode';               // localStorage key
  const OBSERVE_SELECTOR = 'a[href]';
  const EXCLUDE_PREFIXES = ['/checkout', '/cart', '/admin', '/apps', '/account']; // do not append
  const EXCLUDE_SCHEMES = ['mailto:', 'tel:', 'javascript:'];

  // get discount from URL, or fallback to localStorage
  function getDiscountFromUrl() {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get(PARAM_NAME);
    } catch (e) {
      return null;
    }
  }

  function saveDiscountToStorage(code) {
    if (!code) return;
    try {
      localStorage.setItem(STORAGE_KEY, code);
      // Also set window property so other scripts reading it immediately can see it
      window.__promoDiscount = window.__promoDiscount || {};
      window.__promoDiscount.code = code;
    } catch (e) {
      console.warn('promo-links: cannot write localStorage', e);
    }
  }

  function getStoredDiscount() {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch (e) {
      return null;
    }
  }

  // Determine if link is internal (same origin) and not excluded
  function isInternalLink(a) {
    if (!a || !a.href) return false;
    const href = a.getAttribute('href') || '';
    // exclude schemes
    for (const s of EXCLUDE_SCHEMES) {
      if (href.indexOf(s) === 0) return false;
    }
    // ignore anchor-only links like '#section'
    if (href.indexOf('#') === 0) return false;

    try {
      const url = new URL(a.href, location.origin);
      if (url.origin !== location.origin) return false;
      // exclude admin/checkout/etc paths
      for (const p of EXCLUDE_PREFIXES) {
        if (url.pathname.indexOf(p) === 0) return false;
      }
      return true;
    } catch (e) {
      return false;
    }
  }

  // Append discount param to a URL string if not present
  function appendDiscountToUrlString(href, code) {
    if (!code) return href;
    try {
      const url = new URL(href, location.origin);

      // If already has the discount param, don't modify
      if (url.searchParams.get(PARAM_NAME)) return url.toString();

      url.searchParams.set(PARAM_NAME, code);
      return url.toString();
    } catch (e) {
      return href;
    }
  }

  // Update a single <a> element's href if internal and missing discount param
  function updateLinkElement(a, code) {
    if (!isInternalLink(a)) return;
    try {
      // avoid rewriting links that were intentionally flagged to ignore
      if (a.dataset.promoIgnored === 'true') return;

      const newHref = appendDiscountToUrlString(a.href, code);
      // only set if changed (avoid mutating too often)
      if (newHref !== a.href) {
        a.href = newHref;
        a.dataset.promoAttached = 'true';
      }
    } catch (e) {
      // ignore
    }
  }

  // Update all links currently on the page
  function updateAllLinks(code) {
    if (!code) return;
    const anchors = document.querySelectorAll(OBSERVE_SELECTOR);
    anchors.forEach(a => updateLinkElement(a, code));
  }

  // Observe DOM for newly inserted links and update them too
  function observeNewLinks(code) {
    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (!m.addedNodes) continue;
        m.addedNodes.forEach(node => {
          if (node.nodeType !== 1) return;
          if (node.matches && node.matches('a[href]')) {
            updateLinkElement(node, code);
          }
          // also find anchors inside the subtree
          node.querySelectorAll && node.querySelectorAll('a[href]').forEach(a => updateLinkElement(a, code));
        });
      }
    });

    observer.observe(document.documentElement || document.body, {
      childList: true,
      subtree: true
    });

    // also re-run on history navigation (pushState/popstate) for SPA behavior
    const origPush = history.pushState;
    history.pushState = function () {
      origPush.apply(this, arguments);
      setTimeout(() => updateAllLinks(getStoredDiscount()), 30);
    };
    window.addEventListener('popstate', () => {
      setTimeout(() => updateAllLinks(getStoredDiscount()), 30);
    });
  }

  // Intercept clicks to ensure localStorage is set BEFORE navigation (good for fast nav)
  // and ensure the href contains the discount param (in case a link wasn't updated)
  function interceptClicks(code) {
    document.addEventListener('click', (e) => {
      const a = e.target.closest && e.target.closest('a[href]');
      if (!a) return;
      if (!isInternalLink(a)) return;
      // set storage
      saveDiscountToStorage(code);
      // ensure href has param immediately (safe-guard)
      a.href = appendDiscountToUrlString(a.href, code);
      // allow navigation to proceed
    }, {capture: true});
  }

  // Clean promo param from URL (optional). We will NOT remove it automatically here because user asked
  // it should always be appended wherever they navigate. But we will persist it to localStorage.
  // If you want to clean URL after read, uncomment the removePromoFromUrl() call below.
  function removePromoFromUrl() {
    try {
      const params = new URLSearchParams(window.location.search);
      if (!params.has(PARAM_NAME)) return;
      params.delete(PARAM_NAME);
      const clean = window.location.pathname + (params.toString() ? '?' + params.toString() : '') + window.location.hash;
      window.history.replaceState({}, '', clean);
    } catch (e) { /* ignore */ }
  }

  // Main init flow
  (function init() {
    // 1) capture code from URL immediately if present
    const urlCode = getDiscountFromUrl();
    if (urlCode) {
      saveDiscountToStorage(urlCode);
      // optional: if you want to remove discount param from current URL uncomment:
      // removePromoFromUrl();
    }

    // 2) if we have a stored code, ensure links are updated right away
    const stored = getStoredDiscount();
    if (stored) {
      // run on DOM ready or immediately if document already loaded
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
          updateAllLinks(stored);
          observeNewLinks(stored);
          interceptClicks(stored);
        });
      } else {
        updateAllLinks(stored);
        observeNewLinks(stored);
        interceptClicks(stored);
      }
    } else {
      // If no stored code yet, we still want to watch for links and for future capture
      // (for pages that are reached *after* landing page, but we may pick up the code from URL).
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
          // If URL later contains param (e.g., landing page), save & update immediately
          const codeLater = getDiscountFromUrl() || getStoredDiscount();
          if (codeLater) {
            saveDiscountToStorage(codeLater);
            updateAllLinks(codeLater);
            observeNewLinks(codeLater);
            interceptClicks(codeLater);
          } else {
            // still observe DOM so when page gets the param later or links are injected
            observeNewLinks(getStoredDiscount());
            interceptClicks(getStoredDiscount());
          }
        });
      } else {
        observeNewLinks(getStoredDiscount());
        interceptClicks(getStoredDiscount());
      }
    }
  })();

})();
